import { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Card from "../../common/components/Card";
import ColorPicker from "../../common/components/ColorPicker";
import { Row, Col } from "../../common/components/Grid";
import FormEditor from "../../common/components/GridEditor";
import IconPicker from "../../common/components/IconPicker";
import ReactForm from "../../common/components/ReactForm";
import TextInput from "../../common/components/TextInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { canCreateType, canRemoveType, canUpdateType, createType, removeType, updateType, useTypes } from "../../reducers/typeSlice";
import GroupedSettings from "./GroupedSettings";
import PropTypes from "prop-types";
import Icon from "../../common/components/Icon";
import Color from "color";
import Checkbox from "../../common/components/Checkbox";
import CustomForm from "../../common/components/CustomForm";

const defaultSort = ['type'];

const newTypeTemplate = { customfields: [] };

const TypeDetails = forwardRef(({ value: type, groupCtrl, disabled = false, onChange }, formRef) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();

    return <>
        <ReactForm ref={formRef}>
            <Row>
                <Col md>
                    <TextInput placeholder={t('type_one')} readonly={type.internalid} value={type.type} onChange={value => onChange('type')(value)} disabled={disabled} required></TextInput>
                </Col>
                <Col md>
                    <IconPicker placeholder={t('icon')} value={type.icon} onChange={value => onChange('icon')(value)} disabled={disabled} required></IconPicker>
                </Col>
                <Col md>
                    <ColorPicker placeholder={t('color')} value={type.color} onChange={value => onChange('color')(value)} disabled={disabled} required></ColorPicker>
                </Col>
                { clientConfig.enablePCR && <Col md>
                    <Checkbox placeholder={t('epcr_one')} checked={type.epcr} onChange={value => onChange('epcr')(value)} disabled={disabled}></Checkbox>
                </Col> }
            </Row>
            { clientConfig.typeCustomFields && <CustomForm def={clientConfig.typeCustomFields} data={type.customfields} onChange={value => onChange('customfields')(value)} required></CustomForm> }
            {groupCtrl}
        </ReactForm>
        <Row>
            <Col>
                <Card title={t('customfields')}>
                    <FormEditor def={type.formdef} disabled={disabled} onChange={value => onChange('formdef')(value)}></FormEditor>
                </Card>
            </Col>
        </Row>
        { clientConfig.enableCrews && <Row>
            <Col>
                <Card title={t('crew_customfields')}>
                    <FormEditor def={type.crew_formdef} disabled={disabled} onChange={crew_formdef => onChange('crew_formdef')(crew_formdef)} hideAccess></FormEditor>
                </Card>
            </Col>
        </Row> }
    </>
});

const Types = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const types = useTypes();
    
    const sortFields = useMemo(() => {
        const fields = {
            type: t('type_one')
        };
        if (clientConfig.enableLevels) {
            fields.level = t('level_one')
        }
        if (clientConfig.enableSites) {
            fields.site = t('site_one');
        }
        return fields;
    }, [clientConfig.enableLevels, clientConfig.enableSites, t]);

    const groupingCtrls = useMemo(() => {
        const ctrls = [];
        if (clientConfig.enableLevels) {
            ctrls.unshift({ type: 'level', label: t('level', { count: 1 }), title: t('level', { count: 1 }), field: ['site','level','type'], sortfield: 'level', wildcard: t('anyWildcard') });
        }
        if (clientConfig.enableSites) {
            ctrls.unshift({ type: 'site', label: t('site', { count: 1 }), title: t('site', { count: 1 }), field: ['site','level','type'], sortfield: 'site', wildcard: t('anyWildcard') });
        }
        return ctrls;
    }, [t, clientConfig.enableLevels, clientConfig.enableSites]);
    
    const onAdd = useCallback(type => {
        delete type.internalid;
        return createType(network, type);
    }, [network]);
    const onUpdate = useCallback(delta => {
        return updateType(network, delta);
    }, [network, types]);
    const onRemove = useCallback(type => {
        return removeType(network, type);
    }, [network]);

    return <GroupedSettings Details={TypeDetails} data={types} title={label} uuid={uuid}
            defaultEntry={newTypeTemplate} defaultSort={defaultSort} sortFields={sortFields}
            onClose={onClose} makeLabel={e => {
                let color, backgroundColor;
                if (e.color) {
                    try {
                        color = Color(e.color).isLight() ? '#000000' : '#ffffff';
                        backgroundColor = Color(e.color).hex();
                    } catch {
                        color = '#000000';
                        backgroundColor = '#ffffff'
                    }
                }
                return <Row>
                    <Col className="px-2" xs="auto">
                        <div className="px-2 py-1" style={{ backgroundColor, color }}>{ e.icon ? <Icon literal={e.icon}></Icon> : ' '}</div>
                    </Col>
                    <Col>{e.type}</Col>
                </Row>
            }}
            initialGroupingField="type"
            groupingExclusions={['site','level','internalid']}
            groupingCtrls={groupingCtrls}
            canAdd={entry => canCreateType(credentials, entry)} canUpdate={entry => canUpdateType(credentials, entry)} canRemove={entry => canRemoveType(credentials, entry)}
            onAdd={onAdd} onUpdate={onUpdate} onRemove={onRemove}></GroupedSettings>
}
Types.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}
export default Types;